import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Input,
  List,
  Modal,
  Radio,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  PlusOutlined,
  RobotOutlined,
  SearchOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  authorsApi,
  datasourceApi,
  publishersApi,
  type BookInfoResponse,
  type DatasourceCandidate,
} from "../services/api";
import { usePermissions } from "../hooks/usePermissions";

interface AIBookHelperProps {
  onAccept: (
    bookInfo: BookInfoResponse,
    addedAuthorIds: string[],
    addedPublisherId?: string,
  ) => void;
  onAddMissingAuthor?: (authorName: string) => void;
  onAddMissingPublisher?: (publisherName: string) => void;
  onAuthorAdded?: (callback: (authorId: string) => void) => void;
  onPublisherAdded?: (callback: (publisherId: string) => void) => void;
}

export function AIBookHelper({
  onAccept,
  onAddMissingAuthor,
  onAddMissingPublisher,
  onAuthorAdded,
  onPublisherAdded,
}: AIBookHelperProps) {
  const [searchTitle, setSearchTitle] = useState("");
  const [searchIsbn, setSearchIsbn] = useState("");
  const [candidates, setCandidates] = useState<DatasourceCandidate[]>([]);
  const [bookInfo, setBookInfo] = useState<BookInfoResponse | null>(null);
  const [isCandidateModalOpen, setIsCandidateModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [currentProgress, setCurrentProgress] = useState("");
  const [progressMessages, setProgressMessages] = useState<string[]>([]);
  const [addedAuthorIds, setAddedAuthorIds] = useState<string[]>([]);
  const [addedPublisherId, setAddedPublisherId] = useState<string | undefined>();
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const { canCreate } = usePermissions();

  const { data: authors } = useQuery({
    queryKey: ["authors-all"],
    queryFn: () => authorsApi.list({ page_size: 200 }),
  });

  const { data: publishers } = useQuery({
    queryKey: ["publishers-all"],
    queryFn: () => publishersApi.list({ page_size: 100 }),
  });

  useEffect(() => {
    if (onAuthorAdded) {
      onAuthorAdded((authorId: string) => setAddedAuthorIds((prev) => [...prev, authorId]));
    }
  }, [onAuthorAdded]);

  useEffect(() => {
    if (onPublisherAdded) {
      onPublisherAdded((publisherId: string) => setAddedPublisherId(publisherId));
    }
  }, [onPublisherAdded]);

  useEffect(() => {
    if (!isFetching) {
      setElapsedSeconds(0);
      return;
    }

    const startedAt = Date.now();
    setElapsedSeconds(0);
    const intervalId = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isFetching]);

  const imageOptions = useMemo(() => {
    const fromCandidates = bookInfo?.image_candidates || [];
    if (fromCandidates.length > 0) return fromCandidates;
    if (bookInfo?.cover_url) {
      return [
        {
          image_id: "cover_url",
          source: "datasource",
          url: bookInfo.cover_url,
          thumbnail_url: bookInfo.cover_url,
          label: "Cover",
          kind: "cover",
        },
      ];
    }
    return [];
  }, [bookInfo]);

  const matchAuthorsAndPublisher = (data: BookInfoResponse) => {
    const matchedAuthorIds: string[] = [];
    let matchedPublisherId: string | undefined;

    if (data.suggested_authors && authors?.items) {
      data.suggested_authors.forEach((authorName) => {
        const lower = authorName.toLowerCase();
        const match = authors.items.find((a) => {
          const name = a.name.toLowerCase();
          return name === lower || name.includes(lower) || lower.includes(name);
        });
        if (match) matchedAuthorIds.push(match.id);
      });
    }

    if (data.suggested_publisher && publishers?.items) {
      const lower = data.suggested_publisher.toLowerCase();
      const match = publishers.items.find((p) => {
        const name = p.name.toLowerCase();
        return name === lower || name.includes(lower) || lower.includes(name);
      });
      if (match) matchedPublisherId = match.id;
    }

    return { ...data, author_ids: matchedAuthorIds, publisher_id: matchedPublisherId };
  };

  const formatProgressMessage = (msg: string): string => {
    try {
      const parsed = JSON.parse(msg);
      return parsed.message || parsed.source || msg;
    } catch {
      return msg.replace(/^["']|["']$/g, "");
    }
  };

  const handleSearch = async () => {
    const title = searchTitle.trim();
    const isbn = searchIsbn.trim().replace(/[^0-9]/g, "");
    if (!title && !isbn) {
      message.warning("Wprowadz tytul lub ISBN");
      return;
    }
    if (isbn && !/^\d{13}$/.test(isbn)) {
      message.warning("Wprowadz poprawny ISBN-13");
      return;
    }

    setIsFetching(true);
    setCurrentProgress("Szukanie kandydatow...");
    setProgressMessages([]);

    try {
      const result = await datasourceApi.fetchBookCandidates({
        ...(title ? { title } : {}),
        ...(isbn ? { isbn } : {}),
      });
      const items = result.items || [];
      if (items.length === 0) {
        message.warning("Nie znaleziono kandydatow");
        return;
      }
      setCandidates(items);
      setIsCandidateModalOpen(true);
    } catch (error: unknown) {
      message.error(error instanceof Error && error.message ? error.message : "Nie udalo sie wyszukac kandydatow");
    } finally {
      setIsFetching(false);
      setCurrentProgress("");
    }
  };

  const handleCandidateSelect = async (candidate: DatasourceCandidate) => {
    setIsCandidateModalOpen(false);
    setIsFetching(true);
    setProgressMessages([]);
    setCurrentProgress("Pobieranie szczegolow...");

    try {
      const data = await datasourceApi.fetchBookDetailsStreaming(
        {
          title: searchTitle.trim() || candidate.title || undefined,
          isbn: searchIsbn.trim().replace(/[^0-9]/g, "") || undefined,
          candidate,
        },
        (progressMsg) => {
          const formatted = formatProgressMessage(progressMsg);
          setCurrentProgress(formatted);
          setProgressMessages((prev) => [...prev, formatted]);
        },
      );
      const enriched = matchAuthorsAndPublisher(data);
      const defaultImage = enriched.image_candidates?.[0]?.url || enriched.cover_url || null;
      setSelectedImageUrl(defaultImage);
      setBookInfo(enriched);
      setAddedAuthorIds([]);
      setAddedPublisherId(undefined);
      setIsDetailsModalOpen(true);
    } catch (error: unknown) {
      message.error(error instanceof Error && error.message ? error.message : "Nie udalo sie pobrac szczegolow");
    } finally {
      setIsFetching(false);
      setCurrentProgress("");
      setProgressMessages([]);
    }
  };

  const handleAccept = () => {
    if (!bookInfo) return;
    onAccept({ ...bookInfo, cover_url: selectedImageUrl || bookInfo.cover_url }, addedAuthorIds, addedPublisherId);
    setIsDetailsModalOpen(false);
    setSearchTitle("");
    setSearchIsbn("");
    setCandidates([]);
    setBookInfo(null);
    setSelectedImageUrl(null);
    setAddedAuthorIds([]);
    setAddedPublisherId(undefined);
    message.success("Dane zostaly wypelnione w formularzu");
  };

  const authorsMissing =
    Boolean(bookInfo?.suggested_authors?.length) && (!bookInfo?.author_ids || bookInfo.author_ids.length === 0);
  const publisherMissing = Boolean(bookInfo?.suggested_publisher && !bookInfo.publisher_id);

  return (
    <>
      <Card
        title={
          <Space>
            <RobotOutlined />
            <span>Asystent danych - Pobierz informacje o ksiazce</span>
          </Space>
        }
        style={{ marginBottom: 24 }}
        size="small"
      >
        <Space.Compact style={{ width: "100%" }}>
          <Input
            placeholder="Wprowadz tytul ksiazki"
            value={searchTitle}
            onChange={(e) => setSearchTitle(e.target.value)}
            onPressEnter={handleSearch}
            disabled={isFetching}
          />
          <Input
            placeholder="ISBN-13"
            value={searchIsbn}
            onChange={(e) => setSearchIsbn(e.target.value)}
            onPressEnter={handleSearch}
            disabled={isFetching}
            style={{ maxWidth: 220 }}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={isFetching}>
            Szukaj
          </Button>
        </Space.Compact>

        {isFetching && (
          <div style={{ marginTop: 16, textAlign: "center" }}>
            <Spin />
            <Space style={{ marginTop: 8 }}>
              <span>{currentProgress || "Pracuje..."}</span>
              <Tag color="processing">Czas: {elapsedSeconds} s</Tag>
            </Space>
            {progressMessages.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#595959" }}>
                {progressMessages.slice(-4).map((msg, idx) => (
                  <div key={`${msg}-${idx}`}>{msg}</div>
                ))}
              </div>
            )}
          </div>
        )}

        <Alert
          message="Zrodlo danych"
          description="Najpierw wybierzesz konkretne wydanie, potem okladke i dane do formularza."
          type="info"
          showIcon
          style={{ marginTop: 12 }}
        />
      </Card>

      <Modal
        title="Wybierz ksiazke lub wydanie"
        open={isCandidateModalOpen}
        onCancel={() => setIsCandidateModalOpen(false)}
        footer={null}
        width={900}
      >
        <List
          itemLayout="horizontal"
          dataSource={candidates}
          renderItem={(candidate) => (
            <List.Item
              actions={[
                <Button key="select" type="primary" onClick={() => handleCandidateSelect(candidate)}>
                  Wybierz
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={
                  candidate.thumbnail_url ? (
                    <img src={candidate.thumbnail_url} alt="" style={{ width: 56, height: 84, objectFit: "cover" }} />
                  ) : null
                }
                title={
                  <Space wrap>
                    <Typography.Text strong>{candidate.title || "Bez tytulu"}</Typography.Text>
                    {candidate.year && <Tag>{candidate.year}</Tag>}
                    <Tag color="blue">{candidate.source}</Tag>
                  </Space>
                }
                description={
                  <Space direction="vertical" size={2}>
                    {candidate.creators?.length ? (
                      <Typography.Text type="secondary">{candidate.creators.join(", ")}</Typography.Text>
                    ) : null}
                    {candidate.description && (
                      <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 0 }}>
                        {candidate.description}
                      </Typography.Paragraph>
                    )}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Modal>

      <Modal
        title="Dane pobrane z zewnetrznych zrodel"
        open={isDetailsModalOpen}
        onOk={handleAccept}
        onCancel={() => {
          setIsDetailsModalOpen(false);
          setBookInfo(null);
          setSelectedImageUrl(null);
        }}
        width={800}
        okText="Akceptuj i wypelnij formularz"
        cancelText="Anuluj"
        okButtonProps={{ icon: <CheckOutlined /> }}
        cancelButtonProps={{ icon: <CloseOutlined /> }}
      >
        {bookInfo && (
          <>
            {(authorsMissing || publisherMissing) && (
              <Alert
                title="Brakujace elementy w slowniku"
                description="Niektorzy autorzy lub wydawnictwo nie zostali znalezieni w bazie."
                type="warning"
                icon={<WarningOutlined />}
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Tytul">{bookInfo.title || <i>Brak danych</i>}</Descriptions.Item>
              <Descriptions.Item label="Tytul oryginalny">
                {bookInfo.original_title || <i>Brak danych</i>}
              </Descriptions.Item>
              <Descriptions.Item label="Rok wydania">
                {bookInfo.release_year || <i>Brak danych</i>}
              </Descriptions.Item>
              <Descriptions.Item label="Opis">{bookInfo.description || <i>Brak danych</i>}</Descriptions.Item>
              <Descriptions.Item label="ISBN-13">{bookInfo.isbn_13 || <i>Brak danych</i>}</Descriptions.Item>
              <Descriptions.Item label="Liczba stron">
                {bookInfo.page_count || <i>Brak danych</i>}
              </Descriptions.Item>
              <Descriptions.Item label="Wydawca">
                <Space wrap>
                  {bookInfo.suggested_publisher || <i>Brak danych</i>}
                  {(bookInfo.publisher_id || addedPublisherId) && <Tag color="green">Znaleziono w bazie</Tag>}
                  {publisherMissing && onAddMissingPublisher && (
                    <Button
                      size="small"
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => onAddMissingPublisher(bookInfo.suggested_publisher!)}
                      disabled={!canCreate}
                    >
                      Dodaj do slownika
                    </Button>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Autorzy">
                {bookInfo.suggested_authors?.length ? (
                  <Space wrap>
                    {bookInfo.suggested_authors.map((author) => (
                      <Space key={author}>
                        <Tag>{author}</Tag>
                        {authorsMissing && onAddMissingAuthor && (
                          <Button
                            size="small"
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => onAddMissingAuthor(author)}
                            disabled={!canCreate}
                          >
                            Dodaj
                          </Button>
                        )}
                      </Space>
                    ))}
                  </Space>
                ) : (
                  <i>Brak danych</i>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Gatunki">
                {bookInfo.suggested_genres?.length ? (
                  <Space wrap>{bookInfo.suggested_genres.map((genre) => <Tag key={genre}>{genre}</Tag>)}</Space>
                ) : (
                  <i>Brak danych</i>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Okladka">
                {imageOptions.length ? (
                  <Radio.Group value={selectedImageUrl} onChange={(e) => setSelectedImageUrl(e.target.value)}>
                    <Space wrap align="start">
                      {imageOptions.map((image) => (
                        <Radio.Button key={image.image_id} value={image.url} style={{ height: "auto", padding: 8 }}>
                          <Space direction="vertical" size={4}>
                            <img
                              src={image.thumbnail_url || image.url}
                              alt={image.label || "Okladka"}
                              style={{ width: 90, height: 135, objectFit: "contain", border: "1px solid #d9d9d9" }}
                            />
                            <Typography.Text style={{ maxWidth: 90 }} ellipsis>
                              {image.source}
                            </Typography.Text>
                          </Space>
                        </Radio.Button>
                      ))}
                    </Space>
                  </Radio.Group>
                ) : (
                  <i>Brak danych</i>
                )}
              </Descriptions.Item>
            </Descriptions>
          </>
        )}
      </Modal>
    </>
  );
}
